// src/usuarios/Organigrama.jsx
import React, { useEffect, useMemo, useState } from "react";
import { alpha } from "@mui/material/styles";
import { Global } from "../helpers/Global";
import { useToast } from "../hooks/useToast";

import {
  Box,
  Paper,
  Typography,
  Stack,
  Avatar,
  Chip,
  CircularProgress,
} from "@mui/material";

const CARD_WIDTH = 360;
const CARD_HEIGHT = 78;
const COLUMN_GAP = 36;
const ROW_GAP = 30;

const authOnly = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const nombreCompleto = (usuario) =>
  `${usuario?.nombre || ""} ${usuario?.apellidos || ""}`.trim() || "—";

const construirArbol = (usuarios, gerenteId = null) => {
  return (usuarios || [])
    .filter((u) => (u?.gerente_id ?? null) === gerenteId)
    .sort((a, b) => nombreCompleto(a).localeCompare(nombreCompleto(b), "es"))
    .map((u) => ({
      ...u,
      children: construirArbol(usuarios, u.id),
    }));
};

const contarDescendientes = (nodo) =>
  (nodo?.children || []).reduce(
    (total, child) => total + 1 + contarDescendientes(child),
    0
  );

const getAvatarUrl = (usuario) => {
  if (!usuario?.avatar_url) return null;
  return `${Global.url}${usuario.avatar_url}`;
};

const getIniciales = (usuario) => {
  const nombre = (usuario?.nombre || "").trim();
  const apellidos = (usuario?.apellidos || "").trim();

  const n1 = nombre ? nombre.split(" ")[0]?.[0] || "" : "";
  const a1 = apellidos ? apellidos.split(" ")[0]?.[0] || "" : "";

  return (n1 + a1).toUpperCase() || nombre?.[0]?.toUpperCase() || "?";
};

const UsuarioCard = ({ usuario }) => {
  const avatarSrc = getAvatarUrl(usuario);
  const rol = (usuario?.rol || "").toLowerCase();
  const destacado = rol === "gerente" || rol === "admin";
  const total = contarDescendientes(usuario);

  return (
    <Paper
      elevation={0}
      title={usuario?.email || ""}
      sx={(theme) => ({
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        p: 1,
        borderRadius: 3,
        bgcolor: "#ffffff",
        border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
        boxShadow: `0 5px 16px ${alpha(theme.palette.text.primary, 0.07)}`,
        textAlign: "left",
        overflow: "hidden",
        position: "relative",
        zIndex: 2,
      })}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Avatar
          src={avatarSrc || undefined}
          sx={(theme) => ({
            width: 40,
            height: 40,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            fontWeight: 800,
            fontSize: 14,
            flexShrink: 0,
          })}
        >
          {!avatarSrc ? getIniciales(usuario) : null}
        </Avatar>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            noWrap
            sx={(theme) => ({
              fontWeight: 800,
              lineHeight: 1.15,
              color: theme.palette.text.primary,
              fontSize: 12.8,
            })}
          >
            {nombreCompleto(usuario)}
          </Typography>

          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.6 }}>
            <Chip
              label={usuario?.rol || "—"}
              sx={(theme) => ({
                height: 20,
                fontSize: 10.5,
                fontWeight: 800,
                bgcolor: destacado
                  ? alpha(theme.palette.primary.main, 0.16)
                  : alpha(theme.palette.text.primary, 0.07),
                color: theme.palette.text.primary,
                flexShrink: 0,
              })}
            />

            {total > 0 ? (
              <Typography
                noWrap
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  fontSize: 11.5,
                  fontWeight: 700,
                })}
              >
                {total} a cargo
              </Typography>
            ) : usuario?.email ? (
              <Typography
                noWrap
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  fontSize: 11.5,
                  minWidth: 0,
                })}
              >
                {usuario.email}
              </Typography>
            ) : null}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};

const Rama = ({ nodo, depth = 0 }) => {
  const hasChildren = Array.isArray(nodo?.children) && nodo.children.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        position: "relative",
      }}
    >
      <Box sx={{ position: "relative", flexShrink: 0 }}>
        <UsuarioCard usuario={nodo} />
      </Box>

      {hasChildren ? (
        <Box
          sx={{
            position: "relative",
            ml: `${COLUMN_GAP}px`,
            pl: `${COLUMN_GAP}px`,
          }}
        >
          {/* Línea horizontal desde el padre hasta el tronco de hijos */}
          <Box
            sx={(theme) => ({
              position: "absolute",
              left: -COLUMN_GAP,
              top: CARD_HEIGHT / 2,
              width: COLUMN_GAP,
              height: 2,
              bgcolor: alpha(theme.palette.text.primary, 0.22),
              borderRadius: 999,
            })}
          />

          {/* Tronco vertical de la rama */}
          <Box
            sx={(theme) => ({
              position: "absolute",
              left: 0,
              top: CARD_HEIGHT / 2,
              bottom: CARD_HEIGHT / 2,
              width: 2,
              bgcolor:
                nodo.children.length > 1
                  ? alpha(theme.palette.text.primary, 0.22)
                  : "transparent",
              borderRadius: 999,
            })}
          />

          <Stack spacing={`${ROW_GAP}px`}>
            {nodo.children.map((child) => (
              <Box
                key={child.id}
                sx={{
                  position: "relative",
                }}
              >
                {/* Línea desde tronco vertical hasta cada hijo */}
                <Box
                  sx={(theme) => ({
                    position: "absolute",
                    left: -COLUMN_GAP,
                    top: CARD_HEIGHT / 2,
                    width: COLUMN_GAP,
                    height: 2,
                    bgcolor: alpha(theme.palette.text.primary, 0.22),
                    borderRadius: 999,
                  })}
                />

                <Rama nodo={child} depth={depth + 1} />
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
};

export const Organigrama = () => {
  const toast = useToast();

  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);

      try {
        const res = await fetch(`${Global.url}usuarios/organigrama`, {
          headers: authOnly(),
        });

        const data = await res.json().catch(() => []);

        if (!res.ok) {
          toast.error?.("Error al cargar organigrama.");
          setUsuarios([]);
          return;
        }

        setUsuarios(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error al obtener organigrama:", err);
        toast.error?.("No se pudo conectar con el servidor.");
        setUsuarios([]);
      } finally {
        setLoading(false);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const arbol = useMemo(() => {
    const roots = construirArbol(usuarios);

    return roots.sort((a, b) => {
      const totalB = contarDescendientes(b);
      const totalA = contarDescendientes(a);

      if (totalB !== totalA) return totalB - totalA;

      return nombreCompleto(a).localeCompare(nombreCompleto(b), "es");
    });
  }, [usuarios]);

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Organigrama de la empresa
        </Typography>

        <Paper
          elevation={0}
          sx={(theme) => ({
            p: 2,
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          })}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1 }}>
              <CircularProgress size={20} />
              <Typography sx={{ color: "text.secondary" }}>
                Cargando…
              </Typography>
            </Box>
          ) : arbol.length === 0 ? (
            <Typography
              sx={{
                color: "text.secondary",
                fontStyle: "italic",
                p: 1,
              }}
            >
              No hay datos de organigrama para mostrar.
            </Typography>
          ) : (
            <Box
              sx={{
                height: "calc(100vh - 230px)",
                minHeight: 560,
                overflowY: "auto",
                overflowX: "auto",
                py: 3,
                px: 2,
              }}
            >
              <Stack spacing={6} sx={{ alignItems: "flex-start" }}>
                {arbol.map((nodo) => (
                  <Rama key={nodo.id} nodo={nodo} />
                ))}
              </Stack>
            </Box>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default Organigrama;